// Rungs 0–2 of the ladder (plan §4), each move DERIVED from a stated
// principle and cited; none copies a figure. The moves only PROPOSE: the
// registry (./index.ts) gates them on the melody, grades them, and ranks.
//
// Rung 0 — Colour: the written chord's quality upgraded for the note sung
//   over it (the realizedChordType table, offered instead of applied) or,
//   with no melody, for its FUNCTION (Nettles & Graf defaults). Felts ch. 7
//   calls part of this "correction of faulty chord symbols".
// Rung 1 — Diatonic substitution (Felts ch. 1): same key, same function
//   family — I ↔ iii ↔ vi, ii ↔ IV ↔ V7sus4, V ↔ viiø; minor: i ↔ ♭III,
//   iiø/iv/♭VI, V ↔ vii°. Diatonic APPROACH (Felts ch. 2): the chord before a
//   target re-harmonised note by note with diatonic chords that hold each
//   melody note as a chord tone in one consistent relationship and enter the
//   target by strong root motion.
// Rung 2 — Dominant devices (Felts ch. 3; Levine): tritone substitution,
//   ii–V insertion, secondary dominant, the extended-dominant CHAIN (two
//   dominants by fifths or by half steps into the target), the chromatic
//   ii–V (the pair a half step above or below, just before), backdoor.

import type { ChordTypeId } from '../chords';
import { degreeLabel, qualityFamily } from '../corpus';
import { pitchClassOf, spellNote } from '../notes';
import { heldEnd, melodyIn } from './context';
import {
  dealBeats,
  diatonicSevenths,
  heard,
  heardFromBass,
  isDominant,
  isSus,
  mod12,
  proposal,
  regionAt,
  restate,
  spellRoot,
  split,
  strongRootMotion,
  symbolOf,
  type Family,
  type Proposal,
} from './helpers';
import { melodyFitFor } from './melodyFit';
import { RUNG_3_TO_5_MOVES } from './moves345';
import { RUNG_6_TO_7_MOVES } from './moves67';
import type { ReharmContext, ReharmMove } from './types';

// ---------------------------------------------------------------------------
// Rung 0 — colour
// ---------------------------------------------------------------------------

/** realizedChordType's table, by melody offset from the root. */
const MELODY_COLOUR: Record<number, Partial<Record<ChordTypeId, ChordTypeId>>> = {
  0: { maj7: 'maj6', maj9: 'maj69', minmaj7: 'min6' },
  1: { dom7: 'dom7b9', min7b5: 'min7b5b9' },
  2: { maj7: 'maj9', maj6: 'maj69', dom7: 'dom9', min7: 'min9', min6: 'min69', min7b5: 'min7b59' },
  3: { dom7: 'dom7s9' },
  5: { dom7: 'dom7sus4', dom9: 'dom7sus4' },
  9: { dom7: 'dom13', dom9: 'dom13' },
};

const colour: ReharmMove = {
  id: 'colour',
  rung: 0,
  name: 'Colour',
  hint: 'Same chord, richer symbol.',
  source:
    'realizedChordType (notation/arrangement.ts) offered rather than applied; Felts ch. 7; Nettles & Graf chord-scale defaults for function-driven tensions.',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const region = regionAt(ctx, at);
    const out: Proposal[] = [];
    const seen = new Set<ChordTypeId>([chord.chordType]);
    const offer = (type: ChordTypeId, why: string) => {
      if (seen.has(type)) return;
      seen.add(type);
      out.push(proposal(ctx, colour, at, [restate(chord, ctx.chordBeats[at], type)], why));
    };
    // The colour sounds for as long as the chord does, blank bars included.
    const sung = melodyIn(ctx, ctx.chordStarts[at], heldEnd(ctx, at)).filter(
      (n) => n.onOnset || n.beats >= 0.5,
    );
    const rootPc = pitchClassOf(chord.root);
    for (const note of sung) {
      const upgrade = MELODY_COLOUR[mod12(note.pc - rootPc)]?.[chord.chordType];
      if (upgrade) offer(upgrade, `melody has the ${tensionName(mod12(note.pc - rootPc))}`);
    }
    // The function table reads the chord by its written root and quality, so
    // a hybrid (heard from its bass) takes only what its melody asks for.
    if (region && !heardFromBass(ctx, at)) {
      const roman = ctx.analysis.romans[at];
      const family = qualityFamily(chord.chordType);
      if (family === '7' && !isSus(chord)) {
        if (region.minor && roman === 'V7') offer('dom7b9', 'V in a minor key takes the ♭9');
        if (!region.minor && roman === 'V7') {
          offer('dom9', 'V takes the 9');
          offer('dom13', 'V takes the 13');
        }
        // Any dominant resolving down a fifth may take the altered ninths —
        // Aebersold's two stock dominant colours, the diminished scale (7♭9)
        // and the diminished whole tone (7♯9), LaVerne's default on every V
        // in Tunes You Thought You Knew.
        if (ctx.sequence[at + 1] && mod12(heard(ctx, at + 1).rootPc - rootPc) === 5) {
          offer('dom7s9', 'dominant takes the ♯9 on the way down a fifth');
          offer('dom7b9', 'dominant takes the ♭9 on the way down a fifth');
        }
      }
      if (family === '∆' && roman === 'I∆') {
        offer('maj9', 'tonic takes the 9');
        offer('maj69', '6/9 tonic');
      }
      if (family === 'm' && (roman === 'IIm' || roman === 'VIm'))
        offer('min9', 'ii and vi take the 9');
    }
    return out;
  },
};

function tensionName(offset: number): string {
  return { 0: 'root', 1: '♭9', 2: '9', 3: '♯9', 5: '11', 9: '13' }[offset] ?? String(offset);
}

// ---------------------------------------------------------------------------
// Rung 1 — diatonic substitution
// ---------------------------------------------------------------------------

/** Function families as (degree, family) members, and the catalogue type each
 *  member takes when written. Felts (ch. 1) files V7sus4 in BOTH the
 *  subdominant and the dominant family — a dominant one only when it resolves
 *  straight to I — so the sus row carries a `sus` flag and a `resolving`
 *  condition rather than a second family. */
type Member = {
  degree: number;
  family: Family;
  type: ChordTypeId;
  roman: string;
  /** The dom7sus4 member: matched by TYPE, not by family (it shares the V's). */
  sus?: boolean;
  /** Only a member of this family when the chord resolves directly to I. */
  resolving?: boolean;
};
const MAJOR_FAMILIES: Member[][] = [
  [
    { degree: 0, family: '∆', type: 'maj7', roman: 'I' },
    { degree: 4, family: 'm', type: 'min7', roman: 'iii' },
    { degree: 9, family: 'm', type: 'min7', roman: 'vi' },
  ],
  [
    { degree: 2, family: 'm', type: 'min7', roman: 'ii' },
    { degree: 5, family: '∆', type: 'maj7', roman: 'IV' },
    { degree: 7, family: '7', type: 'dom7sus4', roman: 'V7sus4', sus: true },
  ],
  [
    { degree: 7, family: '7', type: 'dom7', roman: 'V' },
    { degree: 11, family: 'ø', type: 'min7b5', roman: 'viiø' },
    { degree: 7, family: '7', type: 'dom7sus4', roman: 'V7sus4', sus: true, resolving: true },
  ],
];
const MINOR_FAMILIES: Member[][] = [
  [
    { degree: 0, family: 'm', type: 'min7', roman: 'i' },
    { degree: 3, family: '∆', type: 'maj7', roman: '♭III' },
  ],
  [
    { degree: 2, family: 'ø', type: 'min7b5', roman: 'iiø' },
    { degree: 5, family: 'm', type: 'min7', roman: 'iv' },
    { degree: 8, family: '∆', type: 'maj7', roman: '♭VI' },
  ],
  [
    { degree: 7, family: '7', type: 'dom7', roman: 'V' },
    { degree: 11, family: '°', type: 'dim7', roman: 'vii°' },
  ],
];
const FUNCTION_NAMES = ['tonic', 'subdominant', 'dominant'];

const diatonicSub: ReharmMove = {
  id: 'diatonic-sub',
  rung: 1,
  name: 'Diatonic substitution',
  hint: 'Another chord from the key with the same job.',
  source: 'Felts, Reharmonization Techniques ch. 1–2 (simple substitution, diatonic approach).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const region = regionAt(ctx, at);
    if (!region || heardFromBass(ctx, at)) return [];
    const degree = mod12(pitchClassOf(chord.root) - region.tonicPc);
    const family = qualityFamily(chord.chordType);
    const sus = isSus(chord);
    const resolvesToTonic =
      !!ctx.sequence[at + 1] && mod12(heard(ctx, at + 1).rootPc - region.tonicPc) === 0;
    const families = region.minor ? MINOR_FAMILIES : MAJOR_FAMILIES;
    // A member is offered only while its condition holds; a sus original
    // belongs to exactly one family at a time — dominant when it resolves
    // straight to I, subdominant otherwise.
    const present = (m: Member) => !m.resolving || resolvesToTonic;
    const isSelf = (m: Member) =>
      m.degree === degree &&
      m.family === family &&
      !!m.sus === sus &&
      (!m.sus || !!m.resolving === resolvesToTonic);
    const out: Proposal[] = [];
    families.forEach((members, fi) => {
      const self = members.find(isSelf);
      if (!self) return;
      for (const other of members) {
        if (other === self || !present(other)) continue;
        if (other.degree === self.degree && other.type === self.type) continue;
        const root = spellRoot(mod12(region.tonicPc + other.degree), region);
        out.push(
          proposal(
            ctx,
            diatonicSub,
            at,
            [{ root, chordType: other.type, beats: ctx.chordBeats[at] }],
            `${other.roman} for ${self.roman}, same ${FUNCTION_NAMES[fi]} function`,
          ),
        );
      }
    });
    return out;
  },
};

/** The melody notes over a chord's span as SLOTS a chord can sit on: each
 *  onset on a whole beat from the chord's start opens a slot; a note sounding
 *  at the start without an onset there fills the first. Notes shorter than a
 *  beat ride the slot before them. */
function melodySlots(ctx: ReharmContext, at: number): { pc: number; beats: number }[] {
  if (!ctx.melody) return [];
  const start = ctx.chordStarts[at];
  const end = start + ctx.chordBeats[at];
  const notes = ctx.melody
    .filter((n) => n.endBeat > start + 1e-9 && n.startBeat < end - 1e-9)
    .sort((a, b) => a.startBeat - b.startBeat);
  if (notes.length === 0) return [];
  // The first slot always opens at the chord's own onset — a note entering
  // late still needs a chord under the beats before it — so the slots
  // always deal out the chord's whole span.
  const onsets: { at: number; pc: number }[] = [];
  for (const n of notes) {
    const rel = Math.max(0, n.startBeat - start);
    const whole = Math.abs(rel - Math.round(rel)) < 1e-6;
    if (onsets.length === 0) onsets.push({ at: 0, pc: n.pc });
    else if (whole && Math.round(rel) > onsets[onsets.length - 1].at) {
      onsets.push({ at: Math.round(rel), pc: n.pc });
    }
  }
  return onsets.map((o, i) => ({
    pc: o.pc,
    beats: (i + 1 < onsets.length ? onsets[i + 1].at : end - start) - o.at,
  }));
}

/** Felts's lead relationships, the interval the melody note is above the
 *  chord root, in the order the book reaches for them. */
const LEAD_RELATIONSHIPS: { name: string; interval: number[] }[] = [
  { name: '3rd', interval: [3, 4] },
  { name: '7th', interval: [10, 11] },
  { name: 'root', interval: [0] },
  { name: '5th', interval: [6, 7] },
];

const diatonicApproach: ReharmMove = {
  id: 'diatonic-approach',
  rung: 1,
  name: 'Diatonic approach',
  hint: 'Chords from the key, one per melody note, leading into the next chord.',
  source: 'Felts, Reharmonization Techniques ch. 2 (diatonic approach technique).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const next = ctx.sequence[at + 1];
    const region = regionAt(ctx, at);
    if (!next || !region || ctx.chordBeats[at] < 2 || heardFromBass(ctx, at)) return [];
    const targetPc = heard(ctx, at + 1).rootPc;
    const ownPc = pitchClassOf(chord.root);
    // The vocabulary: the key's sevenths plus Felts's one borrowed admission,
    // ♭VII∆ (a darker stand-in for the viiø) in a major key.
    const vocabulary = [
      ...diatonicSevenths(region.minor),
      ...(region.minor ? [] : [{ degree: 10, type: 'maj7' as ChordTypeId }]),
    ].map((d) => ({ pc: mod12(region.tonicPc + d.degree), type: d.type }));
    const family = qualityFamily(chord.chordType);
    const degree = mod12(ownPc - region.tonicPc);
    const sameFamily = (region.minor ? MINOR_FAMILIES : MAJOR_FAMILIES).find((members) =>
      members.some((m) => m.degree === degree && m.family === family),
    );
    // A chord that IS the target, just before it, is an anticipation, not an
    // approach. The diatonic-sub move already offers this chord's own
    // function family, so the single-chord form leaves those out too.
    const usable = vocabulary.filter((v) => v.pc !== targetPc);
    const single = usable.filter(
      (v) => v.pc !== ownPc && !sameFamily?.some((m) => mod12(region.tonicPc + m.degree) === v.pc),
    );
    const beatsAll = ctx.chordBeats[at];
    const out: Proposal[] = [];

    const slots = melodySlots(ctx, at);
    if (slots.length === 0) {
      // No melody to lead: every usable chord that enters the target strongly.
      for (const v of single) {
        if (!strongRootMotion(v.pc, targetPc)) continue;
        out.push(
          proposal(
            ctx,
            diatonicApproach,
            at,
            [{ root: spellRoot(v.pc, region), chordType: v.type, beats: beatsAll }],
            `from the key, into ${next.symbol}`,
          ),
        );
      }
      return out;
    }

    // With a melody: one chord per note, every note in the SAME relationship
    // to its chord (all 3rds, all 7ths…), each chord entering the next by
    // strong root motion and the last one the target. At most one candidate
    // per relationship, which is how the book presents them — and short runs
    // only (Felts: it is easy to make the harmonic rhythm "hyperactive"), so
    // a chord carrying more than four notes is not a target for it.
    if (slots.length > 4) return out;
    const sung = slots;
    // The line's last chord rings on through any bars with no symbol after
    // the chord, so it must also carry whatever the melody sings there.
    const tail = melodyIn(ctx, ctx.chordStarts[at] + beatsAll, heldEnd(ctx, at));
    const fitsTail = (v: { pc: number; type: ChordTypeId }) =>
      melodyFitFor(spellRoot(v.pc, region), v.type, tail, true, {
        dominantResolves: mod12(targetPc - v.pc) === 5,
      }) !== 'none';
    for (const rel of LEAD_RELATIONSHIPS) {
      const line: { pc: number; type: ChordTypeId }[] = [];
      let ok = true;
      for (const [i, slot] of sung.entries()) {
        const fits = usable.filter(
          (v) =>
            rel.interval.includes(mod12(slot.pc - v.pc)) &&
            melodyFitFor(
              spellRoot(v.pc, region),
              v.type,
              [{ pc: slot.pc, beats: 1, onOnset: true, risesAfter: false }],
              true,
            ) === 'chord-tone' &&
            (i < sung.length - 1 || fitsTail(v)),
        );
        const prev = line.at(-1);
        const pick =
          fits.find((v) => (!prev || strongRootMotion(prev.pc, v.pc)) && v.pc !== prev?.pc) ??
          fits.find((v) => v.pc !== prev?.pc);
        if (!pick) {
          ok = false;
          break;
        }
        line.push(pick);
      }
      if (!ok || line.length === 0) continue;
      if (!strongRootMotion(line[line.length - 1].pc, targetPc)) continue;
      // A line that only restates one chord across every slot is one chord —
      // and if that chord is the written one, nothing has been proposed.
      const merged: { pc: number; type: ChordTypeId; beats: number }[] = [];
      line.forEach((v, i) => {
        const last = merged.at(-1);
        if (last && last.pc === v.pc && last.type === v.type) last.beats += sung[i].beats;
        else merged.push({ ...v, beats: sung[i].beats });
      });
      if (merged.length === 1 && merged[0].pc === ownPc && merged[0].type === chord.chordType) {
        continue;
      }
      out.push(
        proposal(
          ctx,
          diatonicApproach,
          at,
          merged.map((v) => ({ root: spellRoot(v.pc, region), chordType: v.type, beats: v.beats })),
          `melody as the ${rel.name} of each chord, into ${next.symbol}`,
        ),
      );
    }
    return out;
  },
};

// ---------------------------------------------------------------------------
// Rung 2 — dominant devices
// ---------------------------------------------------------------------------

const tritoneSub: ReharmMove = {
  id: 'tritone-sub',
  rung: 2,
  name: 'Tritone substitution',
  hint: 'The dominant a tritone away.',
  source: 'Felts ch. 3; Levine, The Jazz Theory Book (Reharmonization: tritone substitution).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    if (!isDominant(chord) || isSus(chord) || heardFromBass(ctx, at)) return [];
    const region = regionAt(ctx, at);
    const rootPc = mod12(pitchClassOf(chord.root) + 6);
    const root = spellRoot(rootPc, region);
    const guide = [
      spellNote(mod12(pitchClassOf(chord.root) + 4), 'flat'),
      spellNote(mod12(pitchClassOf(chord.root) + 10), 'flat'),
    ];
    return [
      proposal(
        ctx,
        tritoneSub,
        at,
        [{ root, chordType: 'dom7', beats: ctx.chordBeats[at] }],
        `same guide tones as ${chord.symbol}: ${guide.join(' and ')}`,
      ),
    ];
  },
};

const iiVInsert: ReharmMove = {
  id: 'ii-v-insert',
  rung: 2,
  name: 'ii–V insertion',
  hint: 'Split the V into ii–V.',
  source: 'Felts ch. 3 (extended II–V patterns); Levine (ii–V insertion).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    if (!isDominant(chord) || isSus(chord) || ctx.chordBeats[at] < 2) return [];
    if (heardFromBass(ctx, at)) return [];
    const rootPc = pitchClassOf(chord.root);
    const minorFamily = (f: Family) => f === 'm' || f === 'ø';
    const isIi = (pc: number, f: Family) => mod12(rootPc - pc) === 5 && minorFamily(f);
    // Already preceded by its ii: nothing to insert. Heard (a hybrid that
    // sounds as the ii) or written — Dm7/G is the ii over the V's own bass.
    const prev = ctx.sequence[at - 1];
    if (prev) {
      const before = heard(ctx, at - 1);
      if (isIi(before.rootPc, before.family)) return [];
      if (isIi(pitchClassOf(prev.root), qualityFamily(prev.chordType))) return [];
    }
    const next = ctx.sequence[at + 1] ? heard(ctx, at + 1) : undefined;
    const region = regionAt(ctx, at);
    const iiPc = mod12(rootPc + 7);
    const targetMinor = !!next && mod12(next.rootPc - rootPc) === 5 && minorFamily(next.family);
    const iiType: ChordTypeId = targetMinor ? 'min7b5' : 'min7';
    const [a, b] = split(ctx, at, 2);
    const root = spellRoot(iiPc, region);
    return [
      proposal(
        ctx,
        iiVInsert,
        at,
        [{ root, chordType: iiType, beats: a }, restate(chord, b)],
        `ii–V instead of ${chord.symbol}`,
      ),
    ];
  },
};

const secondaryDominant: ReharmMove = {
  id: 'secondary-dominant',
  rung: 2,
  name: 'Secondary dominant',
  hint: 'Lead in with its own V7.',
  source:
    'Felts ch. 3 (adding dominant chords, extended dominants); Berklee Book of Jazz Harmony ch. 2.',
  candidates(ctx, at) {
    const target = ctx.sequence[at];
    const prev = ctx.sequence[at - 1];
    if (!prev || ctx.chordBeats[at - 1] < 2) return [];
    // Must sit in the same bar-adjacent position: prev's onset + its beats
    // reaches the target's onset (a held chord across blank bars still counts).
    if (
      prev.barIndex !== target.barIndex &&
      ctx.chordStarts[at - 1] + prev.durationBeats < ctx.chordStarts[at] - 1e-9
    ) {
      return [];
    }
    // Both read as heard: a hybrid target (Dm7/G is a V sus) wants its own
    // V, D7, not the A7 its upper chord would ask for.
    const t = heard(ctx, at);
    const p = heard(ctx, at - 1);
    const vPc = mod12(t.rootPc + 7);
    if (p.family === '7' && p.rootPc === vPc) return []; // already its V
    const region = regionAt(ctx, at);
    const root = spellRoot(vPc, region);
    const [a, b] = split(ctx, at - 1, 2);
    const extended = t.family === '7';
    const targetDegree = region ? degreeLabel(t.rootPc, region.tonicPc) : target.displayRoot;
    return [
      {
        ...proposal(
          ctx,
          secondaryDominant,
          at - 1,
          [restate(prev, a), { root, chordType: 'dom7', beats: b }],
          extended
            ? `dominant a fifth above ${target.symbol}`
            : `V7 of ${targetDegree}, taken from the chord before`,
        ),
      },
    ];
  },
};

const extendedDominants: ReharmMove = {
  id: 'extended-dominants',
  rung: 2,
  name: 'Extended dominants',
  hint: 'A chain of dominants into the next chord.',
  source: 'Felts ch. 3 (extended dominant seventh chords, by fifths and by half steps).',
  candidates(ctx, at) {
    const target = ctx.sequence[at];
    const prev = ctx.sequence[at - 1];
    if (!prev || ctx.chordBeats[at - 1] < 2) return [];
    if (
      prev.barIndex !== target.barIndex &&
      ctx.chordStarts[at - 1] + prev.durationBeats < ctx.chordStarts[at] - 1e-9
    ) {
      return [];
    }
    const region = regionAt(ctx, at);
    const targetPc = heard(ctx, at).rootPc;
    const before = heard(ctx, at - 1);
    const prevPc = before.rootPc;
    const prevIsDominant = before.family === '7';
    const out: Proposal[] = [];
    const chain = (roots: number[], why: string, keepPrev: boolean) => {
      const beats = dealBeats(ctx, at - 1, roots.length + (keepPrev ? 1 : 0));
      const chords = roots.map((pc, i) => ({
        root: spellRoot(pc, region),
        chordType: 'dom7' as ChordTypeId,
        beats: beats[i + (keepPrev ? 1 : 0)],
      }));
      out.push(
        proposal(
          ctx,
          extendedDominants,
          at - 1,
          keepPrev ? [restate(prev, beats[0]), ...chords] : chords,
          why,
        ),
      );
    };
    // By fifths: V7/V then V7 of the target (D7 G7 | C). The chord before is
    // replaced outright when it has two beats, kept as an opener at four.
    const fifths = [mod12(targetPc + 2), mod12(targetPc + 7)];
    // By half steps: two dominants sliding down onto the target (D7 D♭7 | C).
    const halfSteps = [mod12(targetPc + 2), mod12(targetPc + 1)];
    // Not where the chord before already IS the dominant the chain would end on.
    if (!(prevIsDominant && prevPc === fifths[1])) {
      chain(fifths, `dominants in fifths into ${target.symbol}`, false);
      if (ctx.chordBeats[at - 1] >= 4) {
        chain(fifths, `dominants in fifths into ${target.symbol}, after the chord`, true);
      }
    }
    if (!(prevIsDominant && prevPc === halfSteps[1])) {
      chain(halfSteps, `dominants sliding down into ${target.symbol}`, false);
    }
    return out;
  },
};

const chromaticIiV: ReharmMove = {
  id: 'chromatic-ii-v',
  rung: 2,
  name: 'Chromatic ii–V',
  hint: 'The ii–V a half step away, just before the chord.',
  source: 'Felts ch. 3–4 (chromatic ii–V patterns; the displacement special case).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    if (ctx.chordBeats[at] < 4) return [];
    // Not a dominant: a dominant's ii–V is the ii-v-insert / tritone-sub's job.
    if (isDominant(chord) || heardFromBass(ctx, at)) return [];
    const region = regionAt(ctx, at);
    const rootPc = pitchClassOf(chord.root);
    const beats = dealBeats(ctx, at, 3);
    const out: Proposal[] = [];
    for (const [offset, where] of [
      [1, 'above'],
      [-1, 'below'],
    ] as const) {
      const vPc = mod12(rootPc + offset);
      const iiPc = mod12(vPc + 5);
      out.push(
        proposal(
          ctx,
          chromaticIiV,
          at,
          [
            { root: spellRoot(iiPc, region), chordType: 'min7', beats: beats[1] },
            { root: spellRoot(vPc, region), chordType: 'dom7', beats: beats[2] },
            restate(chord, beats[0]),
          ],
          `the ii–V a half step ${where}, then ${chord.symbol}`,
        ),
      );
    }
    return out;
  },
};

const backdoor: ReharmMove = {
  id: 'backdoor',
  rung: 2,
  name: 'Backdoor',
  hint: 'Come home from ♭VII7 instead of V.',
  source:
    'Levine, The Jazz Theory Book (backdoor progression); Felts ch. 5 (modal interchange ♭VII7).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const next = ctx.sequence[at + 1];
    if (!isDominant(chord) || isSus(chord) || !next || heardFromBass(ctx, at)) return [];
    const home = heard(ctx, at + 1);
    if (mod12(home.rootPc - pitchClassOf(chord.root)) !== 5 || home.family !== '∆') return [];
    const region = regionAt(ctx, at + 1);
    const tonicPc = home.rootPc;
    const bVII = spellRoot(mod12(tonicPc + 10), region);
    const iv = spellRoot(mod12(tonicPc + 5), region);
    const out: Proposal[] = [
      proposal(
        ctx,
        backdoor,
        at,
        [{ root: bVII, chordType: 'dom7', beats: ctx.chordBeats[at] }],
        `backdoor into ${next.symbol}`,
      ),
    ];
    if (ctx.chordBeats[at] >= 2) {
      const [a, b] = split(ctx, at, 2);
      out.push(
        proposal(
          ctx,
          backdoor,
          at,
          [
            { root: iv, chordType: 'min7', beats: a },
            { root: bVII, chordType: 'dom7', beats: b },
          ],
          `backdoor ii–V into ${next.symbol}`,
        ),
      );
    }
    return out;
  },
};

export const RUNG_0_TO_2_MOVES: readonly ReharmMove[] = [
  colour,
  diatonicSub,
  diatonicApproach,
  tritoneSub,
  iiVInsert,
  secondaryDominant,
  extendedDominants,
  chromaticIiV,
  backdoor,
];

/** Every registered move, rung order. A new move is a new row here. */
export const REHARM_MOVES: readonly ReharmMove[] = [
  ...RUNG_0_TO_2_MOVES,
  ...RUNG_3_TO_5_MOVES,
  ...RUNG_6_TO_7_MOVES,
];

export { symbolOf as reharmSymbol };
